import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { log, logError, logDebug } from '@utils/logger'
import { getUserFriendlyError, formatValidationErrors } from '@utils/errorMessages'

// Type definitions
interface APIError {
  message: string
  status?: number
  code?: string
}

class APIClient {
  private client: AxiosInstance
  private isRefreshing = false // Flag to prevent concurrent refresh attempts

  constructor() {
    // Auto-detect backend URL based on current origin
    let baseURL = import.meta.env.VITE_API_URL
    const currentOrigin = window.location.origin
    
    // Log for debugging (only in dev mode)
    logDebug('[API] Current origin:', currentOrigin)
    logDebug('[API] VITE_API_URL from env:', import.meta.env.VITE_API_URL || '(not set)')
    
    // Map frontend domains to their corresponding API domains
    // NOTE: Dev frontend now uses production API to avoid cookie conflicts
    const domainMapping: Record<string, string> = {
      'https://scidraft.com': 'https://api.scidraft.com',
      'https://www.scidraft.com': 'https://api.scidraft.com',
      'https://app.scidraft.com': 'https://api.scidraft.com',
      'https://dev.scidraft.com': 'https://api.scidraft.com',  // Dev frontend uses production API
      'https://staging.scidraft.com': 'https://api-staging.scidraft.com',
    }
    
    // Check if we're on a production domain and map to API domain
    if (domainMapping[currentOrigin]) {
      baseURL = domainMapping[currentOrigin]
      logDebug('[API] Detected production domain - using mapped API domain:', baseURL)
    } else {
      // If accessed via Tailscale, ignore VITE_API_URL if it points to localhost
      // This ensures the proxy works correctly when accessed remotely
      // Tailscale IPs are typically in the 100.x.x.x range
      const isTailscaleIP = /^https?:\/\/100\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(currentOrigin)
      const isTailscaleAccess = currentOrigin.includes('.ts.net') || currentOrigin.includes('tailscale') || isTailscaleIP
      const isLocalhostAPI = baseURL && (baseURL.includes('localhost') || baseURL.includes('127.0.0.1'))
      
      if (isTailscaleAccess && isLocalhostAPI) {
        // Override: When accessed via Tailscale, use relative paths for proxy
        // Don't use localhost URL from env as it won't work from remote browsers
        logDebug('[API] Overriding localhost API URL - using relative paths for Tailscale proxy')
        baseURL = ''
      } else if (!baseURL) {
        // If accessed via Tailscale Funnel (single tunnel setup)
        // Use relative paths so Vite's proxy forwards /api/* to localhost:8000
        if (isTailscaleAccess) {
          // Single funnel setup: frontend exposed via Tailscale, backend proxied through Vite
          // Vite dev server proxy will forward /api/* requests to http://localhost:8000
          baseURL = ''
          logDebug('[API] Detected Tailscale origin - using relative paths (Vite proxy)')
        } else if (currentOrigin.includes('https://')) {
          // Other remote HTTPS access - use relative path (requires proxy)
          baseURL = ''
          logDebug('[API] Detected HTTPS origin - using relative paths (proxy required)')
        } else if (currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')) {
          // Local development - direct connection to backend
          baseURL = 'http://localhost:8000'
          logDebug('[API] Local development mode - direct backend connection')
        } else {
          // Fallback: use relative paths
          baseURL = ''
          logDebug('[API] Unknown origin - using relative paths as fallback')
        }
      } else {
        logDebug('[API] Using VITE_API_URL from environment:', baseURL)
      }
    }
    
    // Log the final base URL (only in dev)
    logDebug('[API] Final baseURL:', baseURL || '(relative - will use same origin)')
    
    this.client = axios.create({
      baseURL,
      timeout: 30000, // 30 seconds default timeout (increased for slower operations)
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // SECURITY: Enable cookies (HttpOnly) for authentication
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // SECURITY: Tokens are stored in HttpOnly cookies only
        // Cookies are automatically sent with requests when withCredentials: true
        // No need to manually add Authorization header - backend reads from cookie
        // localStorage fallback removed for security (prevents XSS token theft)

        // For FormData, remove Content-Type header to let axios set it automatically with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }

        // Log the full request URL (only in dev mode)
        const fullUrl = config.baseURL 
          ? (config.baseURL.endsWith('/') ? config.baseURL.slice(0, -1) : config.baseURL) + 
            (config.url?.startsWith('/') ? config.url : '/' + config.url)
          : config.url || ''
        log(`[API] ${config.method?.toUpperCase()} ${fullUrl || config.url}`)
        if (config.data) {
          logDebug('[API] Request data:', config.data)
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        logDebug(`[API] Response:`, response.data)
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any
        
        // SECURITY: Don't try to refresh if:
        // 1. The failed request was the refresh endpoint itself (would cause infinite loop)
        // 2. We're on login/register pages (user isn't authenticated yet)
        // 3. Request was already retried
        // 4. A refresh is already in progress (prevent concurrent refresh attempts)
        const isRefreshEndpoint = originalRequest?.url?.includes('/api/auth/refresh')
        const isAuthPage = window.location.pathname === '/login' || window.location.pathname === '/register'
        
        // Handle 401 errors - try to refresh token first
        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint && !isAuthPage && !this.isRefreshing) {
          originalRequest._retry = true
          this.isRefreshing = true
          
          try {
            // Try to refresh the access token
            const { authAPI } = await import('./auth')
            await authAPI.refreshToken()
            
            // Reset refresh flag
            this.isRefreshing = false
            
            // Retry the original request with new token (cookie is automatically sent)
            return this.client(originalRequest)
          } catch (refreshError) {
            // Reset refresh flag
            this.isRefreshing = false
            
            // Refresh failed - user needs to login again
            // No localStorage cleanup needed - tokens are in HttpOnly cookies only
            // Only redirect if not already on login/register page
            if (!isAuthPage) {
              window.location.href = '/login'
            }
            return Promise.reject(refreshError)
          }
        }
        
        this.handleError(error)
        return Promise.reject(error)
      }
    )
  }

  private handleError(error: AxiosError) {
    const apiError: APIError = {
      message: 'An unexpected error occurred',
      status: error.response?.status,
    }

    if (error.response?.data) {
      const errorData = error.response.data as any
      
      // Handle 402 Payment Required (Insufficient tokens) - detail is an object
      if (error.response?.status === 402 && errorData.detail && typeof errorData.detail === 'object') {
        const detail = errorData.detail
        const required = detail.required
        const current = detail.current_balance
        const recommended = detail.recommended_purchase
        
        if (required !== undefined && current !== undefined) {
          apiError.message = `Insufficient tokens. Required: ${required}, Current: ${current}`
          if (recommended) {
            apiError.message += `. ${recommended}`
          }
        } else {
          apiError.message = detail.error || detail.message || 'Insufficient tokens'
        }
      }
      // Handle FastAPI validation errors (422) - detail is an array of error objects
      else if (Array.isArray(errorData.detail)) {
        // Format validation errors into a readable message
        apiError.message = formatValidationErrors(errorData.detail)
      } else if (error.response?.status === 422 && errorData.detail) {
        // Handle 422 validation errors that might not be in array format
        if (typeof errorData.detail === 'string') {
          apiError.message = getUserFriendlyError(errorData.detail, 422)
        } else if (typeof errorData.detail === 'object' && !Array.isArray(errorData.detail)) {
          // If detail is an object, try to extract a message
          if (errorData.detail.message) {
            apiError.message = String(errorData.detail.message)
          } else if (errorData.detail.error) {
            apiError.message = String(errorData.detail.error)
          } else {
            // Last resort: format as JSON but make it readable
            apiError.message = formatValidationErrors([errorData.detail])
          }
        } else {
          apiError.message = getUserFriendlyError('Validation error. Please check your input.', 422)
        }
      } else {
        // Regular error with string detail - convert to user-friendly message
        const rawMessage = errorData.detail || errorData.message || apiError.message
        // If detail is an object (not already handled), stringify it properly
        if (typeof rawMessage === 'object' && rawMessage !== null) {
          if (rawMessage.error) {
            apiError.message = String(rawMessage.error)
          } else if (rawMessage.message) {
            apiError.message = String(rawMessage.message)
          } else {
            // Try to format as validation error if it has the right structure
            if (rawMessage.type && rawMessage.msg) {
              apiError.message = formatValidationErrors([rawMessage])
            } else {
              apiError.message = getUserFriendlyError(JSON.stringify(rawMessage), error.response?.status)
            }
          }
        } else {
          apiError.message = getUserFriendlyError(String(rawMessage), error.response?.status)
        }
      }
      apiError.code = errorData.code
    } else if (error.request) {
      apiError.message = getUserFriendlyError('Network error', error.response?.status)
    } else {
      // No response and no request - likely a network issue
      apiError.message = getUserFriendlyError(error.message, error.response?.status)
    }

    // Show toast notification for certain errors
    // Don't show 401 errors on login/register pages or when user is logged out
    const isAuthPage = window.location.pathname === '/login' || window.location.pathname === '/register'
    
    // Show user-friendly error messages
    if (error.response?.status === 401) {
      // Don't log 401 errors - they're expected when user is logged out or on auth pages
      // This reduces console noise after logout
      // Only show toast if not on auth pages
      if (!isAuthPage) {
      }
      // Handle logout if needed
      // Return early to avoid logging this expected error
      return
    } else if (error.response?.status === 402) {
      // Payment Required - used here to signal daily AI credit exhaustion
      const errorData = error.response.data as any
      if (errorData.detail && typeof errorData.detail === 'object') {
        // Normalize the error detail to ensure error is always a string
        const normalizedDetail = {
          ...errorData.detail,
          error: typeof errorData.detail.error === 'string' 
            ? errorData.detail.error 
            : (errorData.detail.error?.error || errorData.detail.error?.message || 'Insufficient credits'),
        }
        // Dispatch both new and legacy event names for backward compatibility
        window.dispatchEvent(new CustomEvent('insufficient-credits', {
          detail: normalizedDetail
        }))
        window.dispatchEvent(new CustomEvent('insufficient-tokens', {
          detail: normalizedDetail
        }))
      }
      // Also show a clear toast so every user understands the daily limit.
      try {
        const message =
          'You have reached your free daily AI limit (50 tasks). Please wait until tomorrow. Pro plans with higher limits are coming soon.'
        // Lazy-load toast to avoid static import cycles
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(message, { duration: 6000 })
        }).catch(() => {})
      } catch {
        // ignore toast errors
      }
      return
    } else if (error.response?.status === 429) {
      // Too Many Requests - Rate limit exceeded
      const errorData = error.response.data as any
      if (errorData.detail && typeof errorData.detail === 'object') {
        // Show rate limit notice
        window.dispatchEvent(new CustomEvent('rate-limit-exceeded', {
          detail: errorData.detail
        }))
      } else {
      }
      try {
        const message =
          'You are making requests too quickly. Please slow down and try again in a moment.'
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(message, { duration: 5000 })
        }).catch(() => {})
      } catch {
        // ignore toast errors
      }
      return
    } else if (error.response?.status === 403) {
    } else if (error.response?.status === 404) {
      // Don't show toast for statistics endpoint 404s (endpoint might not be available yet)
      if (!error.config?.url?.includes('/statistics')) {
      }
    } else if (error.response?.status === 422) {
      // Validation error - message is already formatted above
      // Don't show toast on auth pages - error is displayed in the form
      if (!isAuthPage) {
      }
    } else if (error.response?.status === 500 || error.response?.status === 502 || error.response?.status === 503) {
      try {
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(apiError.message || 'Something went wrong on the server. Please try again later.', {
            duration: 5000,
          })
        }).catch(() => {})
      } catch {
        // ignore
      }
    } else if (!error.response) {
      try {
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(apiError.message || 'Network error. Please check your connection and try again.', {
            duration: 5000,
          })
        }).catch(() => {})
      } catch {
        // ignore
      }
    } else {
      // For any other status codes, show the user-friendly message
      try {
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(apiError.message || 'Something went wrong. Please contact the administrator.', {
            duration: 5000,
          })
        }).catch(() => {})
      } catch {
        // ignore
      }
    }
    
    // Log error (sanitized) - but not 401 errors (handled above)
    logError('[API Error]:', apiError)
  }

  // Generic HTTP methods
  async get<T = any>(url: string, params?: any, config?: any): Promise<T> {
    const response = await this.client.get(url, { ...config, params })
    return response.data
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.post(url, data, config)
    return response.data
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.put(url, data, config)
    return response.data
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.patch(url, data, config)
    return response.data
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.client.delete(url)
    return response.data
  }

  // File upload
  async upload<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })

    return response.data
  }

  // File download
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    })

    // Create download link
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/health')
      return true
    } catch (error) {
      return false
    }
  }

  // Get the underlying axios instance for advanced usage
  getClient(): AxiosInstance {
    return this.client
  }
}

// Create singleton instance
export const apiClient = new APIClient()

// Export for use in other API modules
export default apiClient